import React, { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { 
  Star, X, Send, Sparkles, User, MapPin, Loader2
} from 'lucide-react';

// Componente de estrelas clicáveis
function StarRating({ value, onChange, label, disabled = false }) {
  const [hoverValue, setHoverValue] = useState(0);
  
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-muted-foreground">{label}</label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={disabled}
            className="focus:outline-none transition-transform hover:scale-110 disabled:cursor-not-allowed"
            onMouseEnter={() => !disabled && setHoverValue(star)}
            onMouseLeave={() => !disabled && setHoverValue(0)}
            onClick={() => !disabled && onChange(star)}
          >
            <Star
              className={`w-8 h-8 transition-colors ${
                star <= (hoverValue || value)
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ReviewModal({ 
  isOpen, 
  onClose, 
  appointmentData,
  onReviewSubmitted 
}) {
  const [ratings, setRatings] = useState({
    geral: 0,
    lavagem: 0,
    atendimento: 0,
    local: 0
  });
  const [comentario, setComentario] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !appointmentData) return null;

  const handleRatingChange = (field, value) => {
    setRatings(prev => ({ ...prev, [field]: value }));
  };

  const isFormValid = () => {
    return ratings.geral > 0 && 
           ratings.lavagem > 0 && 
           ratings.atendimento > 0 && 
           ratings.local > 0;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast.error('Por favor, avalie todos os critérios');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/reviews`,
        {
          appointment_id: appointmentData.appointment_id,
          rating_geral: ratings.geral,
          rating_lavagem: ratings.lavagem,
          rating_atendimento: ratings.atendimento,
          rating_local: ratings.local,
          comentario: comentario.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Avaliação enviada com sucesso!');
      onReviewSubmitted?.();
      onClose();
    } catch (error) {
      console.error('Erro ao enviar avaliação:', error);
      const message = error.response?.data?.detail || 'Erro ao enviar avaliação';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      day: '2-digit', 
      month: 'long' 
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="review-modal">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Avalie sua lavagem
          </h2>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            data-testid="close-review-modal"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Informações do Serviço */}
        <div className="p-4 bg-slate-50 border-b">
          <p className="font-medium text-primary" data-testid="provider-name">
            {appointmentData.provider_name}
          </p>
          <p className="text-sm text-muted-foreground">
            {formatDate(appointmentData.date)} às {appointmentData.time}
          </p>
        </div>

        {/* Formulário de Avaliação */}
        <div className="p-4 space-y-5">
          {/* Nota Geral */}
          <div className="bg-primary/5 rounded-lg p-4">
            <StarRating
              value={ratings.geral}
              onChange={(val) => handleRatingChange('geral', val)}
              label="Nota geral"
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Como foi sua experiência no geral?
            </p>
          </div>

          {/* Critérios específicos */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 border rounded-lg">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Sparkles className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <StarRating
                  value={ratings.lavagem}
                  onChange={(val) => handleRatingChange('lavagem', val)}
                  label="Qualidade da lavagem"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 border rounded-lg">
              <div className="bg-green-100 p-2 rounded-lg">
                <User className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <StarRating
                  value={ratings.atendimento}
                  onChange={(val) => handleRatingChange('atendimento', val)}
                  label="Atendimento"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 border rounded-lg">
              <div className="bg-orange-100 p-2 rounded-lg">
                <MapPin className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <StarRating
                  value={ratings.local}
                  onChange={(val) => handleRatingChange('local', val)}
                  label="Organização do local"
                  disabled={submitting}
                />
              </div>
            </div>
          </div>

          {/* Comentário */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Comentário (opcional)
            </label>
            <Textarea
              placeholder="Conte como foi sua experiência..."
              value={comentario}
              onChange={(e) => setComentario(e.target.value.slice(0, 500))}
              disabled={submitting}
              rows={3}
              data-testid="review-comment"
            />
            <p className="text-xs text-muted-foreground text-right">
              {comentario.length}/500 caracteres
            </p>
          </div>
        </div>

        {/* Footer / Ações */}
        <div className="sticky bottom-0 bg-white border-t p-4 space-y-2">
          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={!isFormValid() || submitting}
            data-testid="submit-review-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Enviar avaliação
              </>
            )}
          </Button>
          
          <Button
            variant="ghost"
            className="w-full"
            onClick={onClose}
            disabled={submitting}
            data-testid="review-later-btn"
          >
            Avaliar depois
          </Button>
        </div>
      </Card>
    </div>
  );
}
